import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown, X } from "lucide-react";
import { useState } from "react";

const LABEL_STYLE = {
  Thriving: "bg-gold/15 text-gold-dark",
  "Finding Balance": "bg-sage text-forest",
  "Out of Balance": "bg-[#F1E9D8] text-[#7A6A45]",
  "Survival Mode": "bg-forest text-ivory",
  "Insufficient Information": "bg-charcoal/5 text-taupe",
};

const fmt = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

const SummaryBucket = ({ title, items, testid, accent }) => (
  <div className="rounded-[1.75rem] border border-forest/10 bg-white/75 p-6">
    <h4 className={`font-serif text-lg ${accent}`}>{title}</h4>
    {items.length === 0 ? (
      <p className="mt-3 text-sm text-taupe">Nothing stood out here between these two discoveries.</p>
    ) : (
      <ul className="mt-3 flex flex-col gap-3">
        {items.map((it, i) => (
          <li key={i} className="border-l-2 border-gold/40 pl-4">
            <p className="text-sm font-semibold text-forest">{it.title}</p>
            <p className="mt-1 text-xs leading-relaxed text-charcoal/70">{it.sentence}</p>
          </li>
        ))}
      </ul>
    )}
  </div>
);

const ChangeRow = ({ change }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-forest/10 bg-white/75 px-5 py-4">
      <button
        data-testid={`compare-change-${change.question_id.toLowerCase()}`}
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-4 text-left"
      >
        <div>
          <p className="text-sm font-semibold text-forest">{change.title}</p>
          <p className="mt-1 text-xs text-taupe">
            {change.category_name}
            {change.direction && (
              <span className="ml-2 rounded-full bg-sage px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-forest">
                {change.direction}
              </span>
            )}
          </p>
        </div>
        <ChevronDown className={`h-4 w-4 shrink-0 text-taupe transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <p className="pt-3 text-sm leading-relaxed text-charcoal/75">{change.narrative}</p>
            {change.takeaway && (
              <p className="mt-2 text-xs leading-relaxed text-taupe">{change.takeaway}</p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const ComparePanel = ({ data, onClose }) => {
  const c = data.comparison;
  return (
    <div
      data-testid="compare-panel"
      className="rounded-[2rem] border border-forest/10 bg-sage/40 p-7 sm:p-9"
    >
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-serif text-2xl text-forest">
            {fmt(data.older.created_at)} compared with {fmt(data.newer.created_at)}
          </h3>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
            <span className={`rounded-full px-4 py-1.5 font-serif ${LABEL_STYLE[data.older.overall] || ""}`}>
              {data.older.overall}
            </span>
            <span className="text-taupe">→</span>
            <span className={`rounded-full px-4 py-1.5 font-serif ${LABEL_STYLE[data.newer.overall] || ""}`}>
              {data.newer.overall}
            </span>
          </div>
        </div>
        <button
          data-testid="compare-close"
          onClick={onClose}
          className="rounded-full border border-forest/20 p-2 text-forest transition-colors hover:border-gold"
          aria-label="Close comparison"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {!c.comparable ? (
        <p className="mt-5 rounded-2xl bg-white/70 px-5 py-4 text-sm text-charcoal/70">{c.note}</p>
      ) : (
        <>
          <p className="mt-5 text-xs leading-relaxed text-taupe">{c.note}</p>
          {c.status_change && (
            <p className="mt-4 rounded-2xl bg-white/70 px-5 py-4 text-sm leading-relaxed text-forest">
              {c.status_change}
            </p>
          )}

          <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
            {c.dimension_changes.map((d) => (
              <div
                key={d.category}
                className={`rounded-2xl border p-4 text-center ${
                  d.changed ? "border-gold/40 bg-gold/10" : "border-forest/10 bg-white/60"
                }`}
              >
                <p className="text-[11px] font-semibold uppercase tracking-wide text-taupe">{d.name}</p>
                <p className="mt-1.5 text-xs text-charcoal/75">
                  {d.from_label || "n/a"} → {d.to_label || "n/a"}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
            <SummaryBucket
              title="Where You've Noticed More Support"
              items={c.summary.more_support}
              testid="compare-more-support"
              accent="text-gold-dark"
            />
            <SummaryBucket
              title="What's Remained Consistent"
              items={c.summary.consistent}
              testid="compare-consistent"
              accent="text-forest"
            />
            <SummaryBucket
              title="What's Asking for Attention Now"
              items={c.summary.attention_now}
              testid="compare-attention-now"
              accent="text-forest"
            />
          </div>

          {c.changes.length > 0 && (
            <div className="mt-6">
              <h4 className="font-serif text-lg text-forest">Every changed answer</h4>
              <div className="mt-3 flex flex-col gap-3">
                {c.changes.map((ch) => (
                  <ChangeRow key={ch.question_id} change={ch} />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
